import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Check, Shield } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { pricingPlans } from '../mock';

export const PricingSection = ({ onContactClick }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % pricingPlans.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + pricingPlans.length) % pricingPlans.length);
  };

  return (
    <section id="pricing" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-[#3E2723] mb-4">
            Transparent Pricing Plans
          </h2>
          <p className="text-lg text-[#8D6E63] max-w-2xl mx-auto">
            Choose the perfect plan for your trademark protection needs. All plans include expert guidance and professional filing.
          </p>
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pricingPlans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative border-2 transition-all duration-300 hover:shadow-xl ${
                plan.recommended
                  ? 'border-[#3E2723] shadow-lg scale-105'
                  : 'border-[#D7CCC8] hover:border-[#8D6E63]'
              }`}
            >
              {plan.recommended && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#3E2723] text-white px-4 py-1">
                  Recommended
                </Badge>
              )}
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl font-bold text-[#3E2723] mb-2">
                  {plan.name}
                </CardTitle>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-[#3E2723]">{plan.price}</span>
                </div>
                <CardDescription className="text-sm text-[#8D6E63] font-medium">
                  {plan.subtitle}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-4">
                <div className="h-64 overflow-y-auto scrollbar-thin scrollbar-thumb-[#D7CCC8] scrollbar-track-[#EFEBE9]">
                  <ul className="space-y-3">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-[#3E2723] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-[#3E2723]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-xs text-[#8D6E63] mt-4 text-center italic">{plan.note}</p>
              </CardContent>
              <CardFooter className="px-4 pb-6">
                <Button
                  onClick={onContactClick}
                  className={`w-full transition-colors duration-300 ${
                    plan.recommended
                      ? 'bg-[#3E2723] hover:bg-[#5D4037] text-white'
                      : 'bg-[#8D6E63] hover:bg-[#3E2723] text-white'
                  }`}
                >
                  Register My Trademark
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* Mobile Slider */}
        <div className="md:hidden">
          <div className="relative">
            <Card
              className={`border-2 transition-all duration-300 ${
                pricingPlans[currentSlide].recommended
                  ? 'border-[#3E2723] shadow-lg'
                  : 'border-[#D7CCC8]'
              }`}
            >
              {pricingPlans[currentSlide].recommended && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#3E2723] text-white px-4 py-1 z-10">
                  Recommended
                </Badge>
              )}
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-2xl font-bold text-[#3E2723] mb-2">
                  {pricingPlans[currentSlide].name}
                </CardTitle>
                <div className="mb-2">
                  <span className="text-4xl font-bold text-[#3E2723]">
                    {pricingPlans[currentSlide].price}
                  </span>
                </div>
                <CardDescription className="text-sm text-[#8D6E63] font-medium">
                  {pricingPlans[currentSlide].subtitle}
                </CardDescription>
              </CardHeader>
              <CardContent className="px-4">
                <div className="max-h-80 overflow-y-auto scrollbar-thin scrollbar-thumb-[#D7CCC8] scrollbar-track-[#EFEBE9]">
                  <ul className="space-y-3">
                    {pricingPlans[currentSlide].features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check className="w-5 h-5 text-[#3E2723] flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-[#3E2723]">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-xs text-[#8D6E63] mt-4 text-center italic">
                  {pricingPlans[currentSlide].note}
                </p>
              </CardContent>
              <CardFooter className="px-4 pb-6">
                <Button
                  onClick={onContactClick}
                  className="w-full bg-[#3E2723] hover:bg-[#5D4037] text-white transition-colors duration-300"
                >
                  Register My Trademark
                </Button>
              </CardFooter>
            </Card>

            {/* Navigation Arrows */}
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-[#3E2723] text-white p-2 rounded-full shadow-lg hover:bg-[#5D4037] transition-colors"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-[#3E2723] text-white p-2 rounded-full shadow-lg hover:bg-[#5D4037] transition-colors"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>

          {/* Dots Indicator */}
          <div className="flex justify-center gap-2 mt-6">
            {pricingPlans.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                  index === currentSlide ? 'bg-[#3E2723] w-8' : 'bg-[#D7CCC8]'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Trust Badge */}
        <div className="mt-12 flex justify-center">
          <div className="inline-flex items-center gap-3 bg-[#EFEBE9] px-6 py-3 rounded-lg border border-[#D7CCC8]">
            <Shield className="w-5 h-5 text-[#3E2723]" />
            <span className="text-[#3E2723] font-medium">
              Secure Payment | Money-Back Guarantee | Expert Support
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};
